import React from 'react';
import '../../styles/components/AsperaSessionMonitor.scss';
import { SubManager } from '../../classes/SubManager';
import { tracking } from '../../services/TrackingService';
import { AsperaError } from '../../classes/AsperaError';
import { InlineNotification, Link } from '../Carbon';
import { logout } from '../../helpers/starterHelpers';
import { language } from '../../services/LanguageService';
import { modal } from '../../services/ModalService';
import { ModalBodyConfirm } from '../ModalBodyConfirm';
/**
 * React Component
 *
 * The AsperaSessionMonitor provides a basic view for monitoring session data.
 * This component is used to avoid state updates on RouteMonitor which is the parent of all views.
 *
 * @example
 * ```javascript
 * <AsperaSessionMonitor />
 * ```
 */
export class AsperaSessionMonitor extends React.Component {
    constructor() {
        super(...arguments);
        this.state = {
            showSessionEndBanner: false,
            forceHideSession: false,
        };
        this.subManager = new SubManager();
        this.lastSessionEndData = {};
        this.logoutCall = () => {
            const { preLogoutActions } = this.props;
            logout(this.subManager, () => { }, preLogoutActions);
        };
        this.showSessionExpire = () => {
            const modalInfo = modal.prepare();
            this.expireModalId = modalInfo.id;
            const modalProps = {
                modalHeading: language.get('notifications.sessionMayHaveExpired'),
                primaryButtonText: language.get('notifications.reload'),
                hideSecondaryButton: true,
            };
            this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalBodyConfirm, { modalClose: modalInfo.callback, description: language.get('notifications.sessionMayHaveExpiredInfo') })).subscribe({
                next: () => {
                    window.location.reload();
                    this.expireModalId = undefined;
                },
                error: () => {
                    window.location.reload();
                    this.expireModalId = undefined;
                },
            }));
        };
        this.showInactivityModal = () => {
            const modalInfo = modal.prepare();
            this.inactivityModalId = modalInfo.id;
            const modalProps = {
                modalHeading: language.get('notifications.sessionExpireSoon'),
                primaryButtonText: language.get('notifications.staySignedIn'),
                secondaryButtonText: language.get('notifications.signOut'),
            };
            this.subManager.setSubscription(modal.create(modalInfo.id, modalProps, React.createElement(ModalBodyConfirm, { modalClose: modalInfo.callback, description: language.get('notifications.inactivityInfo') })).subscribe({
                next: () => {
                    if (typeof tracking.userSessionActivityCallback === 'function') {
                        tracking.userSessionActivityCallback();
                    }
                    this.inactivityModalId = undefined;
                },
                error: data => {
                    if (data.secondaryClose) {
                        this.logoutCall();
                    }
                    this.inactivityModalId = undefined;
                },
            }));
        };
    }
    get sessionEndBanner() {
        const { forceHideSession } = this.state;
        const tokenExpires = this.lastSessionEndData.tokenExpires;
        if (!forceHideSession && tokenExpires && tokenExpires > 0) {
            const minutes = Math.floor(tokenExpires / 60);
            return React.createElement(InlineNotification, { className: "session-end", kind: "warning", onCloseButtonClick: () => this.setState({ forceHideSession: true }), lowContrast: true, title: minutes ? language.getStyled('notifications.sessionExpiresMinutes', [minutes]) : language.getStyled('notifications.sessionExpiresLessThanMinutes'), subtitle: React.createElement(Link, { onClick: this.logoutCall }, language.get('notifications.reAuthenticate')) });
        }
        return null;
    }
    componentDidMount() {
        this.subManager.setSubscription(tracking.sessionUpdateWatcher.subscribe({
            next: data => {
                const { showSessionEndBanner } = this.state;
                this.lastSessionEndData = data;
                if ((typeof this.lastSessionEndData.inactivityExpiresIn === 'number' && this.lastSessionEndData.inactivityExpiresIn <= 0) ||
                    (typeof this.lastSessionEndData.tokenExpires === 'number' && this.lastSessionEndData.tokenExpires <= 0)) {
                    if (this.inactivityModalId) {
                        modal.remove(this.inactivityModalId);
                    }
                    if (!this.expireModalId) {
                        this.showSessionExpire();
                    }
                    return;
                }
                if (this.lastSessionEndData.inactivityExpiresIn && this.lastSessionEndData.inactivityExpiresIn < 300) {
                    if (!this.inactivityModalId && !this.expireModalId) {
                        this.showInactivityModal();
                    }
                }
                if (this.lastSessionEndData.tokenExpires && this.lastSessionEndData.tokenExpires <= 300) {
                    this.setState({ showSessionEndBanner: true });
                }
                else if (showSessionEndBanner) {
                    this.setState({ showSessionEndBanner: false, forceHideSession: false });
                }
            },
            error: error => {
                new AsperaError('AsperaSessionMonitor: unable to watch for session tracking', error);
            },
        }));
    }
    componentWillUnmount() {
        this.subManager.unsubscribeAll();
    }
    render() {
        const { showSessionEndBanner } = this.state;
        return (React.createElement("div", { className: "aspera-session-monitor" }, showSessionEndBanner && this.sessionEndBanner));
    }
}
